import React, { useId } from 'react';

export interface CharacterConfig {
  bodyShape: 'round' | 'oval' | 'squircle' | 'drop' | 'bean' | 'peanut';
  bodySize: 'small' | 'medium' | 'large';
  bodyColor: string;
  bodyFinish: 'soft' | 'glossy' | 'velvet' | 'cosmic' | 'glass';
  bodyPattern: 'none' | 'freckles' | 'stars' | 'waves' | 'heart';
  aura: 'none' | 'glow' | 'sparkles' | 'hearts' | 'orbit';
  eyeType: 'big-round' | 'small-dot' | 'crescent' | 'star' | 'heart' | 'triangle' | 'square' | 'spiral' | 'glossy' | 'sleepy' | 'diamond';
  eyeColor: string;
  mouthType: 'smile' | 'big-smile' | 'small' | 'surprised' | 'tongue' | 'sad' | 'smirk' | 'uwu';
  cheekColor: string | null;
  headAccessory: 'none' | 'horn' | 'ear' | 'crown' | 'hat' | 'flower' | 'antenna' | 'halo' | 'bow' | 'headphones' | 'leaf';
  extraAccessory: 'none' | 'cape' | 'scarf' | 'wing' | 'tail' | 'satchel' | 'sparkle' | 'float-heart';
  name: string;
  personality: 'happy' | 'calm' | 'naughty' | 'romantic';
}

export const DEFAULT_CONFIG: CharacterConfig = {
  bodyShape: 'round', bodySize: 'medium', bodyColor: '#9C27B0',
  bodyFinish: 'glossy', bodyPattern: 'none', aura: 'glow',
  eyeType: 'glossy', eyeColor: '#2E1065', mouthType: 'smile', cheekColor: '#FF6B9D',
  headAccessory: 'antenna', extraAccessory: 'none',
  name: 'Pixie', personality: 'happy',
};

export const BODY_COLORS: Record<string, string> = {
  purple: '#9C27B0', blue: '#2196F3', pink: '#E91E8C',
  orange: '#FF9800', yellow: '#FFD700', green: '#4CAF50',
  red: '#F44336', teal: '#00BCD4', gray: '#9E9E9E',
  black: '#424242', white: '#EEEEEE', rainbow: 'rainbow',
};

export const CHEEK_COLORS = ['#FF6B9D', '#FFB3BA', '#FFD700', '#FF9800', '#C77DFF', '#00BCD4'];
export const EYE_COLORS = ['#1A0A2E', '#2E1065', '#0F766E', '#1D4ED8', '#7C2D12', '#BE123C', '#111827'];

interface Props {
  config: CharacterConfig;
  size?: number;
  mood?: 'normal' | 'happy' | 'sleeping' | 'sad';
}

// SVG viewBox: 0 0 100 120. Character body center: (50, 65).
// All parts scale together via bodySize transform around (50, 65).

function Eye({ type, isSleeping, cx, cy, eyeColor }: {
  type: CharacterConfig['eyeType']; isSleeping: boolean; cx: number; cy: number; eyeColor: string;
}) {
  if (isSleeping) {
    return <path d={`M${cx - 5},${cy} Q${cx},${cy - 4} ${cx + 5},${cy}`}
      fill="none" stroke={eyeColor} strokeWidth="2.5" strokeLinecap="round" />;
  }
  switch (type) {
    case 'big-round':
      return (
        <g>
          <circle cx={cx} cy={cy} r="7" fill="white" />
          <circle cx={cx + 1} cy={cy + 1} r="3.5" fill={eyeColor} />
          <circle cx={cx + 2} cy={cy - 1} r="1" fill="white" />
        </g>
      );
    case 'small-dot':
      return <circle cx={cx} cy={cy} r="4" fill={eyeColor} />;
    case 'crescent':
      return (
        <g>
          <circle cx={cx} cy={cy} r="6" fill="white" />
          <circle cx={cx + 3} cy={cy - 2} r="5.2" fill={eyeColor} />
        </g>
      );
    case 'star': {
      const allPts = [0, 36, 72, 108, 144, 180, 216, 252, 288, 324].map((a, i) => {
        const r = i % 2 === 0 ? 7 : 3;
        return `${cx + r * Math.sin((a * Math.PI) / 180)},${cy - r * Math.cos((a * Math.PI) / 180)}`;
      });
      return <polygon points={allPts.join(' ')} fill="#FFD700" stroke={eyeColor} strokeWidth="0.8" strokeLinejoin="round" />;
    }
    case 'heart':
      return (
        <path d={`M${cx},${cy + 4} C${cx - 6},${cy} ${cx - 8},${cy - 5} ${cx - 5.5},${cy - 7} C${cx - 3},${cy - 9} ${cx},${cy - 6} ${cx},${cy - 4.5} C${cx},${cy - 6} ${cx + 3},${cy - 9} ${cx + 5.5},${cy - 7} C${cx + 8},${cy - 5} ${cx + 6},${cy} ${cx},${cy + 4} Z`}
          fill="#E91E8C" />
      );
    case 'triangle':
      return <polygon points={`${cx},${cy - 7} ${cx + 6.5},${cy + 4} ${cx - 6.5},${cy + 4}`} fill={eyeColor} />;
    case 'square':
      return <rect x={cx - 5} y={cy - 5} width="10" height="10" rx="2.5" fill={eyeColor} />;
    case 'spiral':
      return (
        <g>
          <circle cx={cx} cy={cy} r="5.5" fill="none" stroke={eyeColor} strokeWidth="2.5" />
          <circle cx={cx} cy={cy} r="2" fill={eyeColor} />
        </g>
      );
    case 'glossy':
      return (
        <g>
          <ellipse cx={cx} cy={cy} rx="7.2" ry="8" fill="white" opacity="0.96" />
          <ellipse cx={cx + 0.8} cy={cy + 1.2} rx="4.4" ry="5" fill={eyeColor} />
          <ellipse cx={cx - 1.6} cy={cy - 2.8} rx="2.1" ry="2.7" fill="white" opacity="0.94" />
          <circle cx={cx + 2.8} cy={cy - 0.9} r="1" fill="white" opacity="0.78" />
          <path d={`M${cx - 5.5},${cy + 5} Q${cx},${cy + 8.5} ${cx + 5.8},${cy + 5}`}
            fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth="1.2" strokeLinecap="round" />
        </g>
      );
    case 'sleepy':
      return (
        <g>
          <path d={`M${cx - 6},${cy + 1} Q${cx},${cy + 5} ${cx + 6},${cy + 1}`}
            fill="none" stroke={eyeColor} strokeWidth="2.5" strokeLinecap="round" />
          <path d={`M${cx - 4},${cy - 2} Q${cx},${cy + 1} ${cx + 4},${cy - 2}`}
            fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth="1.2" strokeLinecap="round" />
        </g>
      );
    case 'diamond':
      return (
        <g>
          <polygon points={`${cx},${cy - 8} ${cx + 7},${cy} ${cx},${cy + 8} ${cx - 7},${cy}`}
            fill="white" opacity="0.95" />
          <polygon points={`${cx},${cy - 5.4} ${cx + 4.8},${cy} ${cx},${cy + 5.4} ${cx - 4.8},${cy}`}
            fill={eyeColor} />
          <path d={`M${cx - 1.5},${cy - 4.5} L${cx + 2.2},${cy - 0.8}`}
            stroke="white" strokeWidth="1.3" strokeLinecap="round" opacity="0.85" />
        </g>
      );
    default:
      return <circle cx={cx} cy={cy} r="7" fill="white" />;
  }
}

function Mouth({ type, isSad }: { type: CharacterConfig['mouthType']; isSad: boolean }) {
  const cx = 50, cy = 76;
  if (isSad) {
    return <path d={`M${cx - 10},${cy + 5} Q${cx},${cy - 3} ${cx + 10},${cy + 5}`}
      fill="none" stroke="#1a0a2e" strokeWidth="2.5" strokeLinecap="round" />;
  }
  switch (type) {
    case 'smile':
      return <path d={`M${cx - 10},${cy} Q${cx},${cy + 9} ${cx + 10},${cy}`}
        fill="none" stroke="#1a0a2e" strokeWidth="2.5" strokeLinecap="round" />;
    case 'big-smile':
      return (
        <g>
          <path d={`M${cx - 12},${cy - 1} Q${cx},${cy + 13} ${cx + 12},${cy - 1} Z`} fill="white" />
          <path d={`M${cx - 12},${cy - 1} Q${cx},${cy + 13} ${cx + 12},${cy - 1}`}
            fill="none" stroke="#1a0a2e" strokeWidth="2" strokeLinecap="round" />
        </g>
      );
    case 'small':
      return <path d={`M${cx - 6},${cy} Q${cx},${cy + 5} ${cx + 6},${cy}`}
        fill="none" stroke="#1a0a2e" strokeWidth="2" strokeLinecap="round" />;
    case 'surprised':
      return (
        <g>
          <ellipse cx={cx} cy={cy + 2} rx="5" ry="6.5" fill="#1a0a2e" />
          <ellipse cx={cx} cy={cy + 1.5} rx="3" ry="4.5" fill="white" opacity="0.65" />
        </g>
      );
    case 'tongue':
      return (
        <g>
          <path d={`M${cx - 9},${cy} Q${cx},${cy + 8} ${cx + 9},${cy}`}
            fill="none" stroke="#1a0a2e" strokeWidth="2.5" strokeLinecap="round" />
          <ellipse cx={cx} cy={cy + 9} rx="5" ry="3.5" fill="#FF6B9D" />
        </g>
      );
    case 'sad':
      return <path d={`M${cx - 10},${cy + 5} Q${cx},${cy - 3} ${cx + 10},${cy + 5}`}
        fill="none" stroke="#1a0a2e" strokeWidth="2.5" strokeLinecap="round" />;
    case 'smirk':
      return (
        <path d={`M${cx - 8},${cy + 1} C${cx - 2},${cy + 7} ${cx + 8},${cy + 4} ${cx + 11},${cy - 1}`}
          fill="none" stroke="#1a0a2e" strokeWidth="2.5" strokeLinecap="round" />
      );
    case 'uwu':
      return (
        <g>
          <path d={`M${cx - 9},${cy} Q${cx - 5},${cy + 5} ${cx - 1},${cy}`}
            fill="none" stroke="#1a0a2e" strokeWidth="2.1" strokeLinecap="round" />
          <path d={`M${cx + 1},${cy} Q${cx + 5},${cy + 5} ${cx + 9},${cy}`}
            fill="none" stroke="#1a0a2e" strokeWidth="2.1" strokeLinecap="round" />
          <circle cx={cx} cy={cy + 4} r="1.2" fill="#1a0a2e" opacity="0.7" />
        </g>
      );
    default:
      return <path d={`M${cx - 10},${cy} Q${cx},${cy + 9} ${cx + 10},${cy}`}
        fill="none" stroke="#1a0a2e" strokeWidth="2.5" strokeLinecap="round" />;
  }
}

function HeadAccessory({ type, bodyColor }: { type: CharacterConfig['headAccessory']; bodyColor: string }) {
  switch (type) {
    case 'horn':
      return (
        <g>
          <polygon points="37,38 31,14 46,30" fill="#7C4DFF" />
          <polygon points="63,38 69,14 54,30" fill="#7C4DFF" />
          <line x1="38" y1="32" x2="35" y2="20" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" />
          <line x1="62" y1="32" x2="65" y2="20" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" />
        </g>
      );
    case 'ear':
      return (
        <g>
          <ellipse cx="32" cy="47" rx="8" ry="15" fill={bodyColor} transform="rotate(-12 32 47)" />
          <ellipse cx="32" cy="47" rx="4.5" ry="9.5" fill="#FFB3BA" opacity="0.75" transform="rotate(-12 32 47)" />
          <ellipse cx="68" cy="47" rx="8" ry="15" fill={bodyColor} transform="rotate(12 68 47)" />
          <ellipse cx="68" cy="47" rx="4.5" ry="9.5" fill="#FFB3BA" opacity="0.75" transform="rotate(12 68 47)" />
        </g>
      );
    case 'crown':
      return (
        <g>
          <polygon points="30,41 30,22 40,33 50,16 60,33 70,22 70,41" fill="#FFD700" />
          <circle cx="50" cy="17" r="3.5" fill="#FF6B9D" />
          <circle cx="40" cy="32" r="2.2" fill="#C77DFF" />
          <circle cx="60" cy="32" r="2.2" fill="#00E5FF" />
          <rect x="30" y="38" width="40" height="5" rx="2" fill="#FFC107" />
        </g>
      );
    case 'hat':
      return (
        <g>
          <rect x="32" y="38" width="36" height="5" rx="2.5" fill="#2C2C2C" />
          <rect x="40" y="12" width="20" height="27" rx="4" fill="#1a1a1a" />
          <rect x="40" y="32" width="20" height="7" fill="#E91E8C" />
          <ellipse cx="50" cy="16" rx="4" ry="2" fill="rgba(255,255,255,0.1)" />
        </g>
      );
    case 'flower':
      return (
        <g>
          {[0, 72, 144, 216, 288].map((deg, i) => {
            const r = (deg * Math.PI) / 180;
            return <circle key={i} cx={50 + Math.cos(r) * 9} cy={28 + Math.sin(r) * 9} r="6.5" fill="#FFB3BA" opacity="0.85" />;
          })}
          <circle cx="50" cy="28" r="7.5" fill="#FFD700" />
          <circle cx="50" cy="28" r="3.5" fill="#FF9800" />
        </g>
      );
    case 'antenna':
      return (
        <g>
          <line x1="39" y1="38" x2="33" y2="20" stroke="#7C4DFF" strokeWidth="3" strokeLinecap="round" />
          <circle cx="33" cy="19" r="3.5" fill="#C77DFF" />
          <circle cx="33" cy="19" r="1.5" fill="white" opacity="0.6" />
          <line x1="61" y1="38" x2="67" y2="20" stroke="#7C4DFF" strokeWidth="3" strokeLinecap="round" />
          <circle cx="67" cy="19" r="3.5" fill="#C77DFF" />
          <circle cx="67" cy="19" r="1.5" fill="white" opacity="0.6" />
        </g>
      );
    case 'halo':
      return (
        <g>
          <ellipse cx="50" cy="24" rx="21" ry="6" fill="none" stroke="#FFE7A3" strokeWidth="3.2" opacity="0.95" />
          <ellipse cx="50" cy="23" rx="15" ry="3" fill="none" stroke="white" strokeWidth="1" opacity="0.42" />
        </g>
      );
    case 'bow':
      return (
        <g transform="translate(0 -1)">
          <path d="M42,34 C30,25 29,42 41,40 C39,37 39,36 42,34 Z" fill="#FF6B9D" />
          <path d="M58,34 C70,25 71,42 59,40 C61,37 61,36 58,34 Z" fill="#FF6B9D" />
          <rect x="45" y="31" width="10" height="10" rx="3" fill="#E91E8C" />
          <path d="M34,33 C37,31 40,31 43,35" fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth="1.3" strokeLinecap="round" />
          <path d="M66,33 C63,31 60,31 57,35" fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth="1.3" strokeLinecap="round" />
        </g>
      );
    case 'headphones':
      return (
        <g>
          <path d="M28,56 C29,31 71,31 72,56" fill="none" stroke="#231133" strokeWidth="4.4" strokeLinecap="round" />
          <path d="M31,55 C32,36 68,36 69,55" fill="none" stroke="rgba(255,255,255,0.26)" strokeWidth="1.5" strokeLinecap="round" />
          <rect x="21" y="52" width="10" height="19" rx="5" fill="#2E1065" />
          <rect x="69" y="52" width="10" height="19" rx="5" fill="#2E1065" />
          <rect x="23" y="55" width="5" height="13" rx="2.5" fill="#C77DFF" opacity="0.7" />
          <rect x="72" y="55" width="5" height="13" rx="2.5" fill="#C77DFF" opacity="0.7" />
        </g>
      );
    case 'leaf':
      return (
        <g>
          <path d="M48,40 C49,31 53,25 60,20" fill="none" stroke="#2E7D32" strokeWidth="2.5" strokeLinecap="round" />
          <path d="M58,19 C73,16 72,35 58,37 C50,30 51,23 58,19 Z" fill="#6EE7B7" />
          <path d="M57,36 C61,29 64,24 70,19" fill="none" stroke="#15803D" strokeWidth="1.4" strokeLinecap="round" opacity="0.7" />
          <circle cx="47" cy="40" r="2.2" fill="#2E7D32" />
        </g>
      );
    default:
      return null;
  }
}

function ExtraAccessory({ type, bodyColor, layer }: {
  type: CharacterConfig['extraAccessory']; bodyColor: string; layer: 'back' | 'front';
}) {
  if (type === 'none') return null;
  switch (type) {
    case 'cape':
      if (layer !== 'back') return null;
      return (
        <path d="M24,60 L8,112 L50,90 L92,112 L76,60"
          fill={bodyColor} opacity="0.7"
          style={{ filter: 'brightness(0.65)' }} />
      );
    case 'scarf':
      if (layer !== 'front') return null;
      return (
        <g>
          <ellipse cx="50" cy="91" rx="24" ry="7" fill="#E91E8C" opacity="0.88" />
          <path d="M57,91 L65,114 L60,114 L52,91" fill="#C2185B" opacity="0.88" />
          <ellipse cx="50" cy="91" rx="24" ry="3.5" fill="rgba(255,255,255,0.12)" />
        </g>
      );
    case 'wing':
      if (layer !== 'back') return null;
      return (
        <g>
          <path d="M22,65 C4,44 2,82 16,84 L22,68" fill="rgba(180,200,255,0.22)" stroke="rgba(160,190,255,0.55)" strokeWidth="1.5" />
          <path d="M78,65 C96,44 98,82 84,84 L78,68" fill="rgba(180,200,255,0.22)" stroke="rgba(160,190,255,0.55)" strokeWidth="1.5" />
        </g>
      );
    case 'tail':
      if (layer !== 'front') return null;
      return (
        <g>
          <path d="M63,92 C80,96 90,86 84,75" fill="none" stroke={bodyColor} strokeWidth="5.5" strokeLinecap="round" />
          <circle cx="84" cy="74" r="5.5" fill={bodyColor} />
          <circle cx="84" cy="74" r="2.5" fill="rgba(255,255,255,0.25)" />
        </g>
      );
    case 'satchel':
      if (layer !== 'front') return null;
      return (
        <g>
          <path d="M29,54 C41,75 55,86 72,93" fill="none" stroke="rgba(255,236,196,0.72)" strokeWidth="4" strokeLinecap="round" />
          <rect x="56" y="78" width="22" height="18" rx="6" fill="#B45309" />
          <path d="M58,83 H76" stroke="#FDE68A" strokeWidth="1.8" strokeLinecap="round" opacity="0.78" />
          <circle cx="67" cy="89" r="2.1" fill="#FDE68A" />
        </g>
      );
    case 'sparkle':
      if (layer !== 'front') return null;
      return (
        <g fill="#FFE7A3">
          <path d="M22,48 L25,55 L32,58 L25,61 L22,68 L19,61 L12,58 L19,55 Z" opacity="0.95" />
          <path d="M78,36 L80,41 L85,43 L80,45 L78,50 L76,45 L71,43 L76,41 Z" opacity="0.86" />
          <circle cx="82" cy="78" r="2.2" fill="#FFFFFF" opacity="0.7" />
        </g>
      );
    case 'float-heart':
      if (layer !== 'front') return null;
      return (
        <g>
          <path d="M80,42 C73,37 72,29 78,27 C82,25 85,29 86,32 C88,29 91,25 95,27 C101,30 99,38 86,46 Z"
            fill="#FF6B9D" opacity="0.92" />
          <circle cx="82" cy="31" r="1.8" fill="white" opacity="0.65" />
        </g>
      );
    default:
      return null;
  }
}

function CharacterAura({ type, bodyColor }: { type: CharacterConfig['aura']; bodyColor: string }) {
  if (type === 'none') return null;
  if (type === 'glow') {
    return (
      <g opacity="0.48">
        <circle cx="50" cy="64" r="39" fill={bodyColor} opacity="0.18" />
        <ellipse cx="50" cy="72" rx="42" ry="34" fill="none" stroke={bodyColor} strokeWidth="1.4" opacity="0.32" />
      </g>
    );
  }
  if (type === 'sparkles') {
    return (
      <g fill="#FFE7A3" opacity="0.92">
        <path d="M17,34 L20,41 L27,44 L20,47 L17,54 L14,47 L7,44 L14,41 Z" />
        <path d="M83,28 L85,33 L90,35 L85,37 L83,42 L81,37 L76,35 L81,33 Z" opacity="0.78" />
        <path d="M88,83 L90,88 L95,90 L90,92 L88,97 L86,92 L81,90 L86,88 Z" opacity="0.7" />
        <circle cx="14" cy="78" r="2.2" fill="white" opacity="0.6" />
      </g>
    );
  }
  if (type === 'hearts') {
    return (
      <g fill="#FF6B9D" opacity="0.82">
        <path d="M17,54 C10,49 11,41 16,40 C20,39 22,42 23,45 C25,42 28,39 32,40 C37,42 37,50 23,60 Z" />
        <path d="M77,35 C72,31 73,25 77,24 C80,23 83,26 84,28 C85,26 88,23 91,24 C96,26 95,32 84,40 Z" opacity="0.78" />
        <path d="M80,88 C75,84 76,78 80,77 C83,76 86,79 87,81 C88,79 91,76 94,77 C99,79 98,85 87,93 Z" opacity="0.62" />
      </g>
    );
  }
  return (
    <g opacity="0.75">
      <ellipse cx="50" cy="66" rx="43" ry="17" fill="none" stroke="#FFE7A3" strokeWidth="1.4" strokeDasharray="5 5"
        transform="rotate(-12 50 66)" />
      <circle cx="83" cy="55" r="3.2" fill="#FFE7A3" />
      <circle cx="18" cy="77" r="2.5" fill="white" opacity="0.72" />
    </g>
  );
}

function BodyPattern({ type, bodyColor }: { type: CharacterConfig['bodyPattern']; bodyColor: string }) {
  if (type === 'none') return null;
  if (type === 'freckles') {
    return (
      <g fill="rgba(255,255,255,0.36)">
        <circle cx="38" cy="45" r="1.5" />
        <circle cx="58" cy="42" r="1.2" />
        <circle cx="67" cy="64" r="1.4" />
        <circle cx="31" cy="64" r="1.2" />
        <circle cx="49" cy="86" r="1.5" />
      </g>
    );
  }
  if (type === 'stars') {
    return (
      <g fill="#FFE7A3" opacity="0.64">
        <path d="M37,45 L39,50 L44,52 L39,54 L37,59 L35,54 L30,52 L35,50 Z" />
        <path d="M64,67 L66,71 L70,73 L66,75 L64,79 L62,75 L58,73 L62,71 Z" opacity="0.74" />
        <circle cx="60" cy="47" r="1.8" fill="white" opacity="0.75" />
      </g>
    );
  }
  if (type === 'waves') {
    return (
      <g fill="none" stroke="rgba(255,255,255,0.24)" strokeWidth="2" strokeLinecap="round">
        <path d="M28,60 C35,55 42,65 49,60 C56,55 63,65 70,60" />
        <path d="M30,73 C37,68 44,78 51,73 C58,68 65,78 72,73" opacity="0.85" />
        <path d="M34,47 C40,43 46,51 52,47 C58,43 64,51 70,47" opacity="0.62" />
      </g>
    );
  }
  return (
    <g>
      <path d="M50,78 C35,67 34,51 44,48 C49,46 52,50 54,55 C57,50 62,46 67,49 C77,55 69,70 50,78 Z"
        fill="#FF6B9D" opacity="0.28" />
      <path d="M50,73 C39,65 39,54 46,52 C50,51 52,54 54,58 C56,54 60,51 64,53 C71,57 65,67 50,73 Z"
        fill="rgba(255,255,255,0.18)" />
      <circle cx="52" cy="56" r="1.8" fill={bodyColor} opacity="0.24" />
    </g>
  );
}

function FinishOverlay({ finish, glassGradientId }: {
  finish: CharacterConfig['bodyFinish']; glassGradientId: string;
}) {
  if (finish === 'soft') {
    return <ellipse cx="40" cy="52" rx="6" ry="4" fill="rgba(255,255,255,0.12)" />;
  }
  if (finish === 'velvet') {
    return (
      <g>
        <path d="M31,77 C40,88 60,89 70,77" fill="none" stroke="rgba(35,17,51,0.18)" strokeWidth="8" strokeLinecap="round" />
        <path d="M35,47 C42,40 57,39 64,47" fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="4" strokeLinecap="round" />
      </g>
    );
  }
  if (finish === 'cosmic') {
    return (
      <g>
        <circle cx="38" cy="48" r="1.5" fill="white" opacity="0.75" />
        <circle cx="61" cy="52" r="1.1" fill="#FFE7A3" opacity="0.82" />
        <circle cx="69" cy="72" r="1.4" fill="#C4B5FD" opacity="0.8" />
        <path d="M34,81 C43,76 52,87 62,80" fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="2" strokeLinecap="round" />
      </g>
    );
  }
  if (finish === 'glass') {
    return (
      <g>
        <ellipse cx="50" cy="62" rx="26" ry="30" fill={`url(#${glassGradientId})`} opacity="0.42" />
        <path d="M35,45 C43,35 59,35 67,45" fill="none" stroke="white" strokeWidth="4.2" strokeLinecap="round" opacity="0.42" />
        <path d="M32,81 C44,91 59,91 70,80" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" opacity="0.2" />
      </g>
    );
  }
  return (
    <path
      d="M34,47 C41,36 58,35 66,45"
      fill="none"
      stroke="rgba(255,255,255,0.32)"
      strokeWidth="5"
      strokeLinecap="round"
      opacity="0.78"
    />
  );
}

export default function PetCharacter({ config, size = 100, mood = 'normal' }: Props) {
  const c: CharacterConfig = { ...DEFAULT_CONFIG, ...config };
  const rawId = useId().replace(/:/g, '');
  const isSleeping = mood === 'sleeping';
  const isSad = mood === 'sad';
  const rainbowId = `rainbowBody-${rawId}`;
  const bodyGradientId = `bodyGradient-${rawId}`;
  const cheekGlowId = `cheekGlow-${rawId}`;
  const shadowId = `softShadow-${rawId}`;
  const glassGradientId = `glassSheen-${rawId}`;
  const fill = c.bodyColor === 'rainbow' ? `url(#${rainbowId})` : `url(#${bodyGradientId})`;
  const bodyBaseColor = c.bodyColor === 'rainbow' ? '#C77DFF' : c.bodyColor;
  const s = c.bodySize === 'small' ? 0.82 : c.bodySize === 'large' ? 1.18 : 1.0;
  const scaleTransform = `translate(50,65) scale(${s}) translate(-50,-65)`;

  const renderBodyShape = () => {
    switch (c.bodyShape) {
      case 'oval':
        return (
          <>
            <ellipse cx="50" cy="65" rx="22" ry="30" fill={fill} />
            <ellipse cx="40" cy="50" rx="5.5" ry="3.5" fill="rgba(255,255,255,0.18)" />
          </>
        );
      case 'squircle':
        return (
          <>
            <rect x="24" y="38" width="52" height="52" rx="20" fill={fill} />
            <ellipse cx="38" cy="50" rx="7" ry="4.5" fill="rgba(255,255,255,0.18)" />
          </>
        );
      case 'drop':
        return (
          <>
            <path d="M50,35 C30,42 22,57 22,68 C22,84 35,95 50,95 C65,95 78,84 78,68 C78,57 70,42 50,35 Z" fill={fill} />
            <ellipse cx="40" cy="50" rx="6" ry="4" fill="rgba(255,255,255,0.18)" />
          </>
        );
      case 'bean':
        return (
          <>
            <path d="M32,42 C18,54 23,83 42,93 C59,102 80,85 76,63 C73,43 50,33 32,42 Z" fill={fill} />
            <ellipse cx="39" cy="52" rx="6" ry="4" fill="rgba(255,255,255,0.18)" transform="rotate(-18 39 52)" />
          </>
        );
      case 'peanut':
        return (
          <>
            <path d="M35,38 C22,43 21,60 34,67 C22,74 28,92 43,96 C58,100 67,89 65,78 C80,76 84,59 75,47 C66,35 49,33 35,38 Z" fill={fill} />
            <ellipse cx="39" cy="49" rx="6" ry="3.7" fill="rgba(255,255,255,0.18)" transform="rotate(-14 39 49)" />
          </>
        );
      default: // round
        return (
          <>
            <circle cx="50" cy="65" r="28" fill={fill} />
            <ellipse cx="40" cy="52" rx="6" ry="4" fill="rgba(255,255,255,0.18)" />
          </>
        );
    }
  };

  return (
    <svg
      viewBox="0 0 100 120"
      width={size}
      height={size * 1.2}
      style={{ overflow: 'visible', display: 'block' }}
    >
      <defs>
        <filter id={shadowId} x="-35%" y="-35%" width="170%" height="180%">
          <feDropShadow dx="0" dy="8" stdDeviation="6" floodColor="#000000" floodOpacity="0.22" />
        </filter>
        <radialGradient id={bodyGradientId} cx="34%" cy="24%" r="78%">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.34" />
          <stop offset="34%" stopColor={bodyBaseColor} />
          <stop offset="100%" stopColor={bodyBaseColor} stopOpacity="0.72" />
        </radialGradient>
        <radialGradient id={cheekGlowId} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={c.cheekColor || '#FF6B9D'} stopOpacity="0.95" />
          <stop offset="100%" stopColor={c.cheekColor || '#FF6B9D'} stopOpacity="0" />
        </radialGradient>
        <linearGradient id={glassGradientId} x1="18%" y1="12%" x2="82%" y2="92%">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.75" />
          <stop offset="42%" stopColor="#FFFFFF" stopOpacity="0.08" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0.22" />
        </linearGradient>
        <linearGradient id={rainbowId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FF6B9D" />
          <stop offset="33%" stopColor="#FFD700" />
          <stop offset="66%" stopColor="#7dff8f" />
          <stop offset="100%" stopColor="#00E5FF" />
        </linearGradient>
      </defs>

      <CharacterAura type={c.aura} bodyColor={bodyBaseColor} />
      <ellipse cx="50" cy="108" rx="28" ry="8" fill="rgba(0,0,0,0.18)" />

      <g transform={scaleTransform} filter={`url(#${shadowId})`}>
        {/* Back layer accessories */}
        <ExtraAccessory type={c.extraAccessory} bodyColor={bodyBaseColor} layer="back" />

        {/* Tiny arms behind the face */}
        <path d="M27,67 C15,68 12,78 19,82 C24,85 29,80 31,72" fill={fill} opacity="0.86" />
        <path d="M73,67 C85,68 88,78 81,82 C76,85 71,80 69,72" fill={fill} opacity="0.86" />

        {/* Body */}
        {renderBodyShape()}

        <BodyPattern type={c.bodyPattern} bodyColor={bodyBaseColor} />
        <FinishOverlay finish={c.bodyFinish} glassGradientId={glassGradientId} />

        {/* Cheeks */}
        {c.cheekColor && (
          <g opacity="0.72">
            <ellipse cx="32" cy="72" rx="10" ry="7" fill={`url(#${cheekGlowId})`} />
            <ellipse cx="68" cy="72" rx="10" ry="7" fill={`url(#${cheekGlowId})`} />
            <ellipse cx="33" cy="72" rx="5.5" ry="3.5" fill={c.cheekColor} opacity="0.65" />
            <ellipse cx="67" cy="72" rx="5.5" ry="3.5" fill={c.cheekColor} opacity="0.65" />
          </g>
        )}

        {/* Eyes */}
        <Eye type={c.eyeType} isSleeping={isSleeping} cx={38} cy={58} eyeColor={c.eyeColor} />
        <Eye type={c.eyeType} isSleeping={isSleeping} cx={62} cy={58} eyeColor={c.eyeColor} />

        {/* Mouth */}
        <Mouth type={c.mouthType} isSad={isSad} />

        {/* Head accessory */}
        <HeadAccessory type={c.headAccessory} bodyColor={bodyBaseColor} />

        {/* Feet */}
        <ellipse cx="38" cy="94" rx="10" ry="5" fill={bodyBaseColor} opacity="0.88" />
        <ellipse cx="62" cy="94" rx="10" ry="5" fill={bodyBaseColor} opacity="0.88" />
        <ellipse cx="35" cy="92" rx="3" ry="1.6" fill="rgba(255,255,255,0.24)" />
        <ellipse cx="59" cy="92" rx="3" ry="1.6" fill="rgba(255,255,255,0.24)" />

        {/* Front layer accessories */}
        <ExtraAccessory type={c.extraAccessory} bodyColor={bodyBaseColor} layer="front" />
      </g>
    </svg>
  );
}
